import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { useMentionComposer } from "@/hooks/use-mention-composer";
import { MentionListbox } from "@/components/project/MentionListbox";
import type { UserLookup } from "@/hooks/use-users";
import type { SendChannelMessageArgs } from "@/hooks/use-channel-messages";

interface ChatComposerProps {
  channelName: string;
  users: UserLookup[];
  onSend: (args: SendChannelMessageArgs) => Promise<void>;
  disabled?: boolean;
}

/**
 * Skrivefeltet i Chat-rommet. Enter sender, Shift+Enter gir linjeskift, og @
 * åpner listen med ansatte (samme @-regler som Prosjektoppdateringer).
 */
export function ChatComposer({ channelName, users, onSend, disabled }: ChatComposerProps) {
  const composer = useMentionComposer({ users, idPrefix: "chat-mention" });
  const { body, setBody } = composer;
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const canSend = !sending && !disabled && (body.trim().length > 0 || file !== null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [body]);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await onSend({ body: body.trim(), file, mentionedUserIds: composer.resolveMentionedIds() });
      composer.reset();
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch {
      // Feilen vises av mutasjonens onError; teksten blir stående så den kan sendes igjen.
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (composer.handleKeyDown(e)) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <div className="relative border-t-[1.5px] border-border px-5 pb-4 pt-3">
      <MentionListbox composer={composer} className="bottom-[calc(100%-4px)] left-5" />
      {file && (
        <div className="mb-2 flex items-center gap-2 rounded-md bg-muted/60 px-2 py-1.5 text-xs">
          <SrIcon name="paperclip" className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">{file.name}</span>
          <button
            type="button"
            aria-label="Fjern vedlegg"
            onClick={() => {
              setFile(null);
              if (fileRef.current) fileRef.current.value = "";
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <SrIcon name="x" className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div className="flex items-end gap-2 rounded-[10px] border-[1.5px] border-[var(--sr-revemoerk)] p-1.5 pl-2">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Legg ved fil"
          className="h-9 w-9 shrink-0 text-muted-foreground"
          onClick={() => fileRef.current?.click()}
          disabled={sending || disabled}
        >
          <SrIcon name="paperclip" className="h-[18px] w-[18px]" />
        </Button>
        <label htmlFor="chat-composer" className="sr-only">
          Skriv melding
        </label>
        <textarea
          id="chat-composer"
          ref={inputRef}
          rows={1}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Skriv til ${channelName}`}
          disabled={disabled}
          aria-autocomplete="list"
          aria-controls={composer.mentionsOpen ? composer.listboxId : undefined}
          aria-activedescendant={composer.highlightedUser ? composer.optionId(composer.highlightedUser.id) : undefined}
          className="max-h-40 min-h-[36px] flex-1 resize-none bg-transparent py-2 text-[15px] outline-none"
        />
        <Button
          type="button"
          size="icon"
          aria-label="Send"
          className="h-9 w-11 shrink-0"
          onClick={() => void handleSend()}
          disabled={!canSend}
        >
          {sending ? <SrIcon name="loader2" className="h-4 w-4 animate-spin" /> : <SrIcon name="send" className="h-4 w-4" />}
        </Button>
      </div>
      <p className="ml-0.5 mt-1.5 text-xs text-muted-foreground">Enter sender · Shift+Enter gir linjeskift · @ for å nevne</p>
    </div>
  );
}
